<template>
    <div>
        <div class="container-fluid mt-4 pt-4">
            <!-- Sales Notes modal -->
            <div id="user_note_modal" class="modal">
                <div class="modal-background"></div>
                <div class="modal-card">
                    <header class="modal-card-head">
                        <p class="modal-card-title">Add Sales Note</p>
                        <button
                            class="delete"
                            aria-label="close"
                            @click="closeModal('user_note_modal')"
                        ></button>
                    </header>
                    <section class="modal-card-body">
                        <div class="field">
                            <label class="label">Part Number</label>
                            <div class="control">
                                <div class="select">
                                    <select v-model="note_part_selected" placeholder="Select Part">
                                        <option :value="note_quoted">
                                            Quoted: {{ note_quoted }}
                                        </option>
                                        <option :value="note_requested">
                                            Requested: {{ note_requested }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                        </div>
                        <div class="field">
                            <label class="label">Note</label>
                            <div class="control">
                                <textarea id="usernote_text" class="textarea" rows="5"></textarea>
                            </div>
                        </div>
                    </section>
                    <footer class="modal-card-foot">
                        <button class="button is-success" @click="save_user_note">Save</button>
                        <button class="button" @click="closeModal('user_note_modal')">
                            Cancel
                        </button>
                    </footer>
                </div>
            </div>

            <!-- Price followup modal -->
            <div id="price_followup_modal" class="modal">
                <div class="modal-background"></div>
                <div class="modal-card">
                    <header class="modal-card-head">
                        <p class="modal-card-title">Pricing Followup</p>
                        <button
                            class="delete"
                            aria-label="close"
                            @click="closeModal('price_followup_modal')"
                        ></button>
                    </header>
                    <section class="modal-card-body">
                        <table class="table is-striped is-fullwidth">
                            <thead>
                                <tr>
                                    <th>Requested</th>
                                    <th>Quoted</th>
                                    <th>Competitor Price</th>
                                </tr>
                            </thead>
                            <tbody :key="followupModalKey">
                                <tr v-for="(row, idx) in rows" :key="'pfm_' + idx">
                                    <td>{{ row.part_number }}</td>
                                    <td>{{ row.part_quoted }}</td>
                                    <td>
                                        <input
                                            type="text"
                                            class="input is-small edit_price_input"
                                            :data-quote_id="row.q_id"
                                            placeholder="0.00"
                                            :value="row.price ? row.price : ''"
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </section>
                    <footer class="modal-card-foot">
                        <button class="button is-success" @click="ajax_pricing_followup_edit">
                            Save
                        </button>
                        <button class="button" @click="closeModal('price_followup_modal')">
                            Cancel
                        </button>
                    </footer>
                </div>
            </div>

            <section id="quote_search" class="d-flex justify-content-center">
                <div style="width: 500px">
                    <div class="d-flex justify-content-between my-2">
                        <div class="flex-grow-1 me-2">
                            <input
                                type="text"
                                class="input"
                                placeholder="Quote ID"
                                v-model="sa_id"
                            />
                        </div>
                        <div class="buttons is-right">
                            <button @click="ajax_dashboard_pricing_followup" class="button is-info">
                                Search
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            <section v-if="rows.length > 0" style="margin: 3rem auto">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <div class="d-flex gap-2">
                        <button
                            class="button is-info"
                            :disabled="!selectedRow"
                            @click="openPriceModal"
                        >
                            Edit Prices
                        </button>
                        <button
                            class="button is-info"
                            :disabled="!selectedRow"
                            @click="openNoteModal"
                        >
                            Add Note
                        </button>
                    </div>
                    <div class="buttons is-right mb-0">
                        <button
                            class="button is-info"
                            style="margin: 0.5em 0 0 0"
                            @click="$emit('download-csv', [rows, 'PricingFollowupDashboard', null])"
                        >
                            Download CSV
                        </button>
                    </div>
                </div>
                <ResponsibleDataTable
                    :headers="tableHeaders"
                    :data="rows"
                    @selection-changed="onSelectionChanged"
                />
                <div class="buttons is-right mt-3">
                    <button
                        class="button is-info"
                        style="margin: 0.5em 0 0 0"
                        @click="$emit('download-csv', [rows, 'PricingFollowupDashboard', null])"
                    >
                        Download CSV
                    </button>
                </div>
            </section>
        </div>
    </div>
</template>

<script>
    import moment from 'moment'
    import '@/assets/bulma-switch.min.css'
    import numeral from 'numeral'
    import pricingFollowupService from '@/services/pricingFollowup.service'
    import noteService from '@/services/note.service'
    import ResponsibleDataTable from '@/components/shared/ResponsibleDataTable.vue'

    export default {
        name: 'DashboardPricingFollowup',
        components: {
            ResponsibleDataTable,
        },
        props: {
            search_action_id: {
                type: [String, Number],
                default: '',
            },
        },
        data() {
            return {
                sa_id: '',
                raw_data: [],
                note_data: [],
                note_part_selected: '',
                note_requested: '',
                note_quoted: '',
                followupTableKey: 0,
                followupModalKey: 0,
                selectedRow: null,
            }
        },
        mounted() {
            if (this.search_action_id) {
                this.sa_id = this.search_action_id
                this.ajax_dashboard_pricing_followup()
            }
        },
        computed: {
            tableHeaders() {
                return {
                    id: 'ID',
                    part_number: 'Part Number',
                    part_quoted: 'Part Quoted',
                    quantity: 'Quantity',
                    unit_cost: 'Unit Cost',
                    quote: 'Quote',
                    quote_created: 'Quote Created',
                    price: 'Competitor Price',
                    followup_created: 'Followup Created',
                    notes: 'Notes',
                }
            },
            rows() {
                const cleaned = []
                for (const row of this.raw_data) {
                    const notes = []
                    const parts = [row.part_number, row.part_quoted]
                    for (const note of this.note_data) {
                        if (parts.includes(note.part_number)) {
                            notes.push(note)
                        }
                    }
                    cleaned.push({
                        ...row,
                        id:
                            row.q_id ||
                            row.id ||
                            `${row.part_number || 'part'}-${row.part_quoted || 'quoted'}`,
                        unit_cost: row.unit_cost
                            ? numeral(row.unit_cost).format('$0,0,0,0,0.00')
                            : '',
                        quote: row.quote ? numeral(row.quote).format('$0,0,0,0,0.00') : '',
                        quote_created: row.quote_created
                            ? moment(moment.utc(row.quote_created).toDate())
                                  .local()
                                  .format('YYYY-MM-DD HH:mm')
                            : '',
                        price: row.price ? numeral(row.price).format('$0,0,0,0,0.00') : '',
                        followup_created: row.followup_created
                            ? moment(moment.utc(row.followup_created).toDate())
                                  .local()
                                  .format('YYYY-MM-DD HH:mm')
                            : '',
                        notes,
                    })
                }
                return cleaned
            },
            is_paged() {
                return this.raw_data.length > 10
            },
        },
        watch: {
            rows() {
                this.followupTableKey += 1
            },
        },
        methods: {
            openModal(modal_id) {
                document.getElementById(modal_id)?.classList.add('is-active')
            },
            closeModal(modal_id) {
                document.getElementById(modal_id)?.classList.remove('is-active')
            },
            onCellClick(params) {
                if (params.column.field === 'price') {
                    this.followupModalKey += 1
                    this.openModal('price_followup_modal')
                }
                if (params.column.field === 'notes') {
                    this.note_requested = params.row.part_number
                    this.note_quoted = params.row.part_quoted
                    this.note_part_selected = this.note_quoted || this.note_requested
                    this.openModal('user_note_modal')
                }
            },
            async save_user_note() {
                const textarea = document.getElementById('usernote_text')
                const new_note = textarea?.value || ''
                if (!new_note.trim()) {
                    return
                }
                try {
                    const res = await noteService.createUserNote({
                        note: new_note,
                        part: this.note_part_selected,
                    })
                    if (res?.success && res.notes?.[0]) {
                        this.note_data.push(res.notes[0])
                        this.followupTableKey += 1
                    } else {
                        alert('Error: Note was not created. Please try again.')
                    }
                } catch (error) {
                    console.error(error)
                    alert('Error: Note was not created. Please try again.')
                } finally {
                    if (textarea) {
                        textarea.value = ''
                    }
                    this.closeModal('user_note_modal')
                }
            },
            async ajax_dashboard_pricing_followup() {
                this.raw_data = []
                try {
                    const res = await pricingFollowupService.fetchFollowups(this.sa_id)
                    if (res?.success) {
                        this.raw_data = res.quotes || []
                        this.note_data = res.notes || []
                    }
                } catch (error) {
                    console.error(error)
                }
            },
            update_prices(price_data) {
                for (const pd of price_data || []) {
                    const row = this.raw_data.find((r) => r.q_id == pd.quote_id)
                    if (row) {
                        row.price = pd.price
                    }
                }
                this.followupModalKey += 1
            },
            async ajax_pricing_followup_edit() {
                const inputs = document.getElementsByClassName('edit_price_input')
                const validNumber = new RegExp(/^\d*\.?\d*$/)
                const price_data = []
                for (const ip of inputs) {
                    const quote_id = ip.getAttribute('data-quote_id')
                    const price = ip.value
                    if (!validNumber.test(price)) {
                        alert('Please enter only numbers for the prices  (ex. 12.99)')
                        return
                    }
                    const row = this.rows.find((r) => String(r.q_id) === String(quote_id))
                    if (row && price !== '' && price != row.price) {
                        price_data.push({ quote_id: quote_id, price: price })
                    }
                }
                this.closeModal('price_followup_modal')
                if (price_data.length > 0) {
                    try {
                        const res = await pricingFollowupService.updateFollowupPrices(price_data)
                        if (res?.success) {
                            this.update_prices(res.price_data)
                        }
                    } catch (error) {
                        console.error(error)
                    }
                }
            },
            rowSelectionChanged() {},
        },
    }
</script>

<style scoped>
    .switch[type='checkbox']:focus + label::after,
    .switch[type='checkbox']:focus + label::before,
    .switch[type='checkbox']:focus + label:after,
    .switch[type='checkbox']:focus + label:before {
        outline: none;
    }

    :deep(.myquotes-true) {
        text-transform: capitalize;
    }

    :deep(.myquotes-false) {
        text-transform: capitalize;
        color: red;
    }
</style>
